/**
 * Pure present/parse/validate pipeline. The field-type index is injected (built
 * from `filterConfigs` by field-index.ts in the app; a hand-built map in tests),
 * so this module has no heavy imports and is unit-testable in a node env.
 *
 * Losslessness invariant: present only emits a structured value when the codec
 * re-encodes byte-for-byte (`roundTrips`); otherwise it emits the raw string,
 * which parse passes straight back. So params -> readable -> params is identity.
 */
import type { SearchBrowseParams } from '@aibees/elephant/types';
import { CODECS, roundTrips } from './codecs';
import { MODALITY_READABLE } from './constants';
import {
  isKnownField,
  isPlainObject,
  parseCodecKind,
  presentCodecKind,
  specialFieldKind,
} from './codec-selection';
import { jsonKeyToStoreKey, storeKeyToJsonKey } from './keys';

export type FieldTypeIndex = ReadonlyMap<string, string>;

const READABLE_TO_MODALITY: Record<string, string> = Object.fromEntries(
  Object.entries(MODALITY_READABLE).map(([enumVal, readable]) => [
    readable,
    enumVal,
  ])
);

export function presentParams(
  params: Partial<SearchBrowseParams>,
  index: FieldTypeIndex
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [storeKey, rawValue] of Object.entries(params ?? {})) {
    if (rawValue == null || rawValue === '') continue;
    const raw = String(rawValue);
    const jsonKey = storeKeyToJsonKey(storeKey);

    if (storeKey === 'modality') {
      out[jsonKey] = MODALITY_READABLE[raw] ?? raw;
      continue;
    }

    const codec = CODECS[presentCodecKind(storeKey, index.get(storeKey), raw)];
    out[jsonKey] = roundTrips(codec, raw) ? codec.toReadable(raw) : raw;
  }
  return out;
}

export function parseParams(
  json: Record<string, unknown>
): Partial<SearchBrowseParams> {
  const out: Record<string, string> = {};
  for (const [jsonKey, val] of Object.entries(json)) {
    if (val == null) continue;
    const storeKey = jsonKeyToStoreKey(jsonKey);

    if (storeKey === 'modality' && typeof val === 'string') {
      out[storeKey] = READABLE_TO_MODALITY[val] ?? val;
      continue;
    }
    // present emits a plain string for un-prettified fields — pass it through.
    if (typeof val === 'string') {
      if (val !== '') out[storeKey] = val;
      continue;
    }

    // Field-fixed codec (EBD / growth / keyword companions) wins; otherwise pick
    // by the readable value's shape. Keeps parse the exact inverse of present.
    const kind = specialFieldKind(storeKey) ?? parseCodecKind(val);
    const encoded = CODECS[kind].toStore(val);
    if (encoded !== undefined && encoded !== '') out[storeKey] = encoded;
  }
  return out as Partial<SearchBrowseParams>;
}

/**
 * Structural validation only (per product decision): must be a JSON object and
 * every key must resolve to a field this scope accepts. Deep filter *values* are
 * not validated. Returns human-readable errors; empty array means valid.
 */
export function validateParams(json: unknown, index: FieldTypeIndex): string[] {
  if (!isPlainObject(json)) return ['Payload must be a JSON object.'];
  const errors: string[] = [];
  for (const jsonKey of Object.keys(json)) {
    const storeKey = jsonKeyToStoreKey(jsonKey);
    if (!isKnownField(storeKey, index)) {
      errors.push(`Unknown field: "${jsonKey}".`);
    }
  }
  return errors;
}
